/**
 * Sanity CMS Client Configuration and Data Fetching Functions
 * 
 * This comprehensive module provides:
 * - Sanity client configuration with environment-based settings
 * - Data fetching functions for all content types (pages, products, blog, etc.)
 * - GROQ query definitions for optimized content retrieval
 * - Content validation and error handling
 * - Performance optimizations including CDN usage and caching
 * 
 * Content Types Handled:
 * - Homepage and marketing content
 * - Product catalog (MTB, Road, Gravel bikes)
 * - Blog posts and articles
 * - Gallery images and media
 * - Site settings and configuration
 * - FAQ and support content
 * 
 * Features:
 * - Environment-based configuration (dev/prod)
 * - Graceful error handling for missing content
 * - Optimized GROQ queries for performance
 * - ISR (Incremental Static Regeneration) support
 * 
 * @description Complete Sanity CMS integration with optimized data fetching
 */

import { createClient } from 'next-sanity'

// The API version to use when querying the content lake
export const apiVersion = process.env.NEXT_PUBLIC_SANITY_API_VERSION || '2023-05-03'

// The project ID and dataset from environment variables - must be exported for other modules
export const projectId = process.env.NEXT_PUBLIC_SANITY_PROJECT_ID
export const dataset = process.env.NEXT_PUBLIC_SANITY_DATASET || 'production'

// Set useCdn to false when previewing drafts
export const useCdn = process.env.NODE_ENV === 'production'

// Validate environment variables in development
if (!projectId && process.env.NODE_ENV !== 'production') {
  console.warn('Missing NEXT_PUBLIC_SANITY_PROJECT_ID in .env.local')
}

if (!dataset && process.env.NODE_ENV !== 'production') {
  console.warn('Missing NEXT_PUBLIC_SANITY_DATASET in .env.local')
}

// Create client with environment variables 
// Important: Since the environment variables could be undefined at build time
// we need to handle this gracefully
export const client = createClient({
  projectId,
  dataset,
  apiVersion,
  useCdn,
})

export const previewClient = createClient({
  projectId,
  dataset,
  apiVersion,
  useCdn: false,
  token: process.env.SANITY_API_TOKEN,
})

// Helper function to determine which client to use
export const getClient = (usePreview) => (usePreview ? previewClient : client)

 

/**
 * Fetches site settings from Sanity
 * @returns {Promise<Object|null>} The site settings object or null if not found
 */
export async function getSiteSettings() {
  try {
    const settings = await client.fetch(`
      *[_type == "siteSettings"][0] {
        siteName,
        logo,
        logoWidth,
        logoHeight,
        footerLogoWidth,
        footerLogoHeight,
        emailLogo
      }
    `);
    
    console.log('Fetched site settings:', settings ? 'Success' : 'Not found');
    return settings || null;
  } catch (error) {
    console.error('Error fetching site settings:', error);
    return null;
  }
}

/**
 * Fetches email logo URL from Sanity site settings
 * Returns a fully qualified URL ready to be used in HTML emails
 * @returns {Promise<string|null>} The email logo URL or null if not found
 */
export async function getEmailLogoUrl() {
  try {
    const settings = await getSiteSettings();
    
    if (!settings || !settings.emailLogo) {
      console.warn('No email logo found in site settings');
      // Return fallback logo URL
      return 'https://cdn.sanity.io/images/c0r366pq/production/1d57cc4c87fe2418de93328de69c689f5d2b2bb9-250x125.png';
    }
    
    // Import urlForImage dynamically to avoid circular dependencies
    const { urlForImage } = await import('./image');
    
    // Generate the image URL using Sanity's image URL builder
    const logoUrl = urlForImage(settings.emailLogo, {
      width: 250,
      format: 'png',
      quality: 90
    });
    
    console.log('Generated email logo URL:', logoUrl);
    return logoUrl;
  } catch (error) {
    console.error('Error fetching email logo URL:', error);
    // Return fallback logo URL on error
    return 'https://cdn.sanity.io/images/c0r366pq/production/1d57cc4c87fe2418de93328de69c689f5d2b2bb9-250x125.png';
  }
}

/**
 * Fetches all products from Sanity with language-specific content
 * @param {boolean} preview - Whether to use preview mode
 * @param {string} locale - The locale to fetch content for
 * @param {string} category - Optional category filter (Road, Gravel, MTB)
 * @returns {Promise<Array>} Array of product data
 */
export async function getProducts(preview = false, locale = 'en', category = null) {
  const currentClient = getClient(preview);
  const productLangFields = getProductLanguageFields(locale);
  
  try {
    let query = `
      *[_type == "product"${category ? ` && category == "${category}"` : ''}] | order(titleEN asc) {
        _id,
        "title": ${productLangFields.title},
        slug,
        category,
        productType,
        price,
        "shortBlurb": ${productLangFields.shortBlurb},
        "cardShortDescriptionBold": ${productLangFields.cardShortDescriptionBold},
        "cardShortDescriptionGrey": ${productLangFields.cardShortDescriptionGrey},
        "material": ${productLangFields.material},
        cardImages[] {
          asset-> {
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt
        },
        images[] {
          asset-> {
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt,
          caption
        },
        weight,
        sizes[] {
          label,
          available,
          extraPrice,
          metadata,
          allowPersonalization
        },
        engravingIncluded,
        showReadMore,
        showDownloads,
        detailSections[] {
          "sectionTitle": ${productLangFields.sectionTitle},
          enabled,
          contentType,
          "overview": ${productLangFields.overview},
          referencedFeatures[]-> {
            _id,
            "title": ${getFeatureLanguageFields(locale).title},
            "body": ${getFeatureLanguageFields(locale).body},
            image {
              asset-> {
                _id,
                url,
                metadata {
                  dimensions
                }
              },
              "alt": ${getFeatureLanguageFields(locale).alt}
            },
            categories
          },
          specGroups[] {
            "groupTitle": ${productLangFields.groupTitle},
            specs[] {
              "key": ${productLangFields.specKey},
              value
            }
          },
          geometrySettings {
            showGeometry,
            geometryType
          }
        },
        outOfStock
      }
    `;
    
    const products = await currentClient.fetch(query);
    
    console.log(`Fetched ${products?.length || 0} products for locale '${locale}'${category ? ` in category '${category}'` : ''}`);
    return products || [];
  } catch (error) {
    console.error(`Error fetching products for locale '${locale}':`, error);
    return [];
  }
}

/**
 * Fetches a single product by slug with language-specific content
 * @param {string} slug - The product slug
 * @param {boolean} preview - Whether to use preview mode
 * @param {string} locale - The locale to fetch content for
 * @returns {Promise<Object|null>} The product object or null if not found
 */
export async function getProduct(slug, preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  const productLangFields = getProductLanguageFields(locale);
  
  try {
    if (!slug) {
      console.error('getProduct called with null or undefined slug');
      return null;
    }

    // Remove leading and trailing slashes if present
    const normalizedSlug = slug.toString().replace(/^\/+|\/+$/g, '');
    
    console.log(`Attempting to fetch product with normalized slug: "${normalizedSlug}" for locale: ${locale}`);
    
    const product = await currentClient.fetch(`
      *[_type == "product" && slug.current == $slug][0] {
        _id,
        "title": ${productLangFields.title},
        slug,
        category,
        productType,
        price,
        "shortBlurb": ${productLangFields.shortBlurb},
        "cardShortDescriptionBold": ${productLangFields.cardShortDescriptionBold},
        "cardShortDescriptionGrey": ${productLangFields.cardShortDescriptionGrey},
        "material": ${productLangFields.material},
        cardImages[] {
          asset-> {
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt
        },
        images[] {
          asset-> {
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt,
          caption
        },
        weight,
        sizes[] {
          label,
          available,
          extraPrice,
          metadata,
          allowPersonalization
        },
        engravingIncluded,
        showReadMore,
        showDownloads,
        detailSections[] {
          "sectionTitle": ${productLangFields.sectionTitle},
          enabled,
          contentType,
          "overview": ${productLangFields.overview},
          referencedFeatures[]-> {
            _id,
            "title": ${getFeatureLanguageFields(locale).title},
            "body": ${getFeatureLanguageFields(locale).body},
            image {
              asset-> {
                _id,
                url,
                metadata {
                  dimensions
                }
              },
              "alt": ${getFeatureLanguageFields(locale).alt}
            },
            categories
          },
          specGroups[] {
            "groupTitle": ${productLangFields.groupTitle},
            specs[] {
              "key": ${productLangFields.specKey},
              value
            }
          },
          geometrySettings {
            showGeometry,
            geometryType
          }
        },
        outOfStock
      }
    `, { slug: normalizedSlug });
    
    if (product) {
      console.log(`Found product: ${product.title} (${product._id})`);
    } else {
      console.error(`No product found with slug: "${normalizedSlug}"`);
    }
    
    return product;
  } catch (error) {
    console.error(`Error fetching product with slug ${slug}:`, error);
    return null;
  }
}

/**
 * Fetches all product slugs for static path generation
 * @returns {Promise<Array<string>>} Array of product slugs
 */
export async function getAllProductSlugs() {
  try {
    const slugs = await client.fetch(`
      *[_type == "product"].slug.current
    `);

    // Normalize slugs to ensure they don't have leading slashes
    const normalizedSlugs = slugs.map(slug => 
      slug.startsWith('/') ? slug.substring(1) : slug
    );

    console.log('Fetched all product slugs:', normalizedSlugs);
    return normalizedSlugs || [];
  } catch (error) {
    console.error('Error fetching product slugs:', error);
    return [];
  }
}

// Helper function to merge media assets from English version for products (legacy support)
export function mergeProductMediaFromEnglish(translatedProducts, englishProducts) {
  if (!translatedProducts || !englishProducts || !Array.isArray(translatedProducts)) {
    return translatedProducts;
  }
  
  return translatedProducts.map(translatedProduct => {
    // Find the corresponding English product by slug or title
    const englishProduct = englishProducts.find(ep => 
      ep.slug?.current === translatedProduct.slug?.current || 
      ep.title === translatedProduct.title
    );
    
    if (!englishProduct) return translatedProduct;
    
    // Merge media fields from English product
    const mediaFields = ['cardImages', 'images', 'gallery', 'featuredImage', 'heroImage'];
    const merged = { ...translatedProduct };
    
    mediaFields.forEach(field => {
      if (!merged[field] && englishProduct[field]) {
        merged[field] = englishProduct[field];
        console.log(`Using English ${field} for product: ${translatedProduct.title}`);
      }
    });
    
    return merged;
  });
}

// Helper function to merge media assets from English version
function mergeMediaFromEnglish(translatedData, englishData) {
  if (!translatedData || !englishData) return translatedData;
  
  const merged = { ...translatedData };
  
  // List of media fields that should fallback to English
  const mediaFields = [
    'heroVideo',
    'heroImage', 
    'backgroundImage',
    'featuredImage',
    'gallery',
    'images',
    'video',
    'flowchartImage',
    'processImages'
  ];
  
  // Also handle nested media in sections
  const sectionsWithMedia = [
    'heroSection',
    'gallerySection', 
    'manufacturingSection',
    'philosophySection',
    'introSection'
  ];
  
  // Merge top-level media fields
  mediaFields.forEach(field => {
    if (!merged[field] && englishData[field]) {
      merged[field] = englishData[field];
      console.log(`Using English ${field} for locale content`);
    }
  });
  
  // Merge media in sections
  sectionsWithMedia.forEach(section => {
    if (merged[section] && englishData[section]) {
      mediaFields.forEach(field => {
        if (!merged[section][field] && englishData[section][field]) {
          merged[section][field] = englishData[section][field];
          console.log(`Using English ${field} in ${section} for locale content`);
        }
      });
      
      // Handle gallery arrays specifically
      if (!merged[section].gallery && englishData[section].gallery) {
        merged[section].gallery = englishData[section].gallery;
      }
    }
  });
  
  return merged;
}

// Helper function to fetch page data with locale support
export async function getPageData(type, preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  
  try {
    // First try to get content in the requested locale
    let data = await currentClient.fetch(`*[_type == "${type}" && language == $locale][0]`, { locale });
    
    // If we have data in the requested locale and it's not English, merge media from English
    if (data && locale !== 'en') {
      const englishData = await currentClient.fetch(`*[_type == "${type}" && language == "en"][0]`);
      if (englishData) {
        data = mergeMediaFromEnglish(data, englishData);
        console.log(`Merged media from English version for ${type} in locale '${locale}'`);
      }
    }
    
    // If no content found in requested locale and it's not English, fallback to English
    if (!data && locale !== 'en') {
      console.warn(`No ${type} content found for locale '${locale}', falling back to English`);
      data = await currentClient.fetch(`*[_type == "${type}" && language == "en"][0]`);
    }
    
    // If still no data and we haven't tried the old format, try without language filter (legacy support)
    if (!data) {
      console.warn(`No ${type} content found with language field, trying legacy format`);
      data = await currentClient.fetch(`*[_type == "${type}"][0]`);
    }
    
    return data;
  } catch (error) {
    console.error(`Error fetching ${type} data:`, error);
    return null;
  }
}

// Helper function to fetch home page data
export async function getHomePageData(preview = false, locale = 'en') {
  return getPageData('homePage', preview, locale);
}


/**
 * Fetch road page data from Sanity
 */
export async function getRoadPageData(preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  const featureLangFields = getFeatureLanguageFields(locale);
  
  try {
    
    // Try to get content in requested locale first
    let query = `*[_type == "road" && language == $locale][0]{
      title,
      description,
      heroSection {
        title,
        buttonLabel,
        backgroundImage
      },
      productGrid {
        configureCard {
          title,
          buttonText,
          productLink,
          backgroundImage
        }
      },
      tabs {
        descriptionTab {
          title,
          defaultSections {
            section1 {
              title,
              description,
              imagePosition,
              image
            },
            section2 {
              title,
              description,
              imagePosition,
              image
            },
            section3 {
              title,
              description,
              imagePosition,
              image
            }
          },
          sections[] {
            title,
            description,
            imagePosition,
            image
          }
        },
        featuresTab {
          title,
          features[] {
            title,
            body,
            image
          },
          referencedFeatures[]-> {
            _id,
            "title": ${featureLangFields.title},
            "body": ${featureLangFields.body},
            image {
              asset-> {
                _id,
                url,
                metadata {
                  dimensions
                }
              },
              "alt": ${featureLangFields.alt}
            },
            categories
          }
        },
        geometryTab {
          title,
          image,
          description,
          raceGeometry {
            title,
            rows[] {
              dimension,
              code,
              grcxs,
              grcs,
              grcm,
              grcl,
              grcxl,
              grcxxl
            }
          },
          enduranceGeometry {
            title,
            rows[] {
              dimension,
              code,
              gedxs,
              geds,
              gedm,
              gedl,
              gedxl,
              gedxxl
            }
          }
        },
        certificationsTab {
          title,
          image,
          description
        },
        picturesTab {
          title,
          images[]
        }
      },
      reviewsSection {
        title,
        reviews[] {
          customerName,
          country,
          bikeType,
          rating,
          reviewText,
          customerImages
        }
      }
    }`;
    
    let data = await currentClient.fetch(query, { locale });
    
    // Fallback to English if no content found in requested locale
    if (!data && locale !== 'en') {
      console.warn(`No road page content found for locale '${locale}', falling back to English`);
      query = query.replace('&& language == $locale', '&& language == "en"');
      data = await currentClient.fetch(query);
    }
    
    // Legacy fallback (content without language field)
    if (!data) {
      console.warn('No road page content found with language field, trying legacy format');
      query = query.replace('*[_type == "road" && language == "en"][0]', '*[_type == "road"][0]');
      data = await currentClient.fetch(query);
    }
    
    // Fetch additional reviews from review documents
    const additionalReviews = await getReviewsByCategory('Road');
    
    // Process the data with fallback defaults
    const processedData = {
      ...data,
      // Ensure heroSection has proper backgroundImage
      heroSection: {
        ...data?.heroSection,
        title: data?.heroSection?.title || "Go Grooty",
        buttonLabel: data?.heroSection?.buttonLabel || "Shop the Grooty",
        backgroundImage: ensureValidImageObject(data?.heroSection?.backgroundImage, "/images/road-hero-grooty.jpg")
      },
      // Process product grid with fallbacks for configure card
      productGrid: {
        ...data?.productGrid,
        configureCard: {
          title: "Make it Yours",
          buttonText: "Configure",
          productLink: "/configure",
          ...data?.productGrid?.configureCard
        }
      },
      // Ensure tabs have proper image references
      tabs: {
        ...data?.tabs,
        featuresTab: {
          ...data?.tabs?.featuresTab,
          title: data?.tabs?.featuresTab?.title || "Features",
          features: data?.tabs?.featuresTab?.features || []
        },
        geometryTab: {
          ...data?.tabs?.geometryTab,
          title: data?.tabs?.geometryTab?.title || "Geometry",
          image: ensureValidImageObject(data?.tabs?.geometryTab?.image, "/images/road-grooty-geometry.png"),
          description: data?.tabs?.geometryTab?.description || null,
          raceGeometry: data?.tabs?.geometryTab?.raceGeometry || { title: "Race Geometry", rows: [] },
          enduranceGeometry: data?.tabs?.geometryTab?.enduranceGeometry || { title: "Endurance Geometry", rows: [] }
        },
        certificationsTab: {
          ...data?.tabs?.certificationsTab,
          title: data?.tabs?.certificationsTab?.title || "Certifications",
          image: ensureValidImageObject(data?.tabs?.certificationsTab?.image, "/images/road-certificationGrooty.jpg"),
          description: data?.tabs?.certificationsTab?.description || null
        }
      },
      // Combine existing reviews with new review documents
      reviewsSection: {
        title: data?.reviewsSection?.title || 'Customer Reviews',
        reviews: [
          ...(data?.reviewsSection?.reviews || []), // Existing embedded reviews
          ...additionalReviews // New review documents
        ]
      }
    };
    
    return processedData;
  } catch (error) {
    console.error('Error fetching road page data:', error);
    return null;
  }
}

// Helper function to ensure we have a valid image object
function ensureValidImageObject(imageData, fallbackPath) {
  // If we have valid Sanity image data, return it
  if (imageData && typeof imageData === 'object' && imageData.asset && imageData.asset._ref) {
    return imageData;
  }
  
  // If we have a string, it might be a path already
  if (typeof imageData === 'string') {
    return imageData;
  }
  
  // Otherwise create a simple object with the fallback path
  // This will be handled by our enhanced urlForImage function
  return { url: fallbackPath };
}

/**
 * Fetch gravel page data from Sanity
 */
export async function getGravelPageData(preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  const featureLangFields = getFeatureLanguageFields(locale);
  
  try {
    let query = `*[_type == "gravel" && language == $locale][0]{
      title,
      description,
      heroSection {
        title,
        buttonLabel,
        backgroundImage
      },
      productGrid {
        configureCard {
          title,
          buttonText,
          productLink,
          backgroundImage
        }
      },
      tabs {
        descriptionTab {
          title,
          defaultSections {
            section1 {
              title,
              description,
              imagePosition,
              image
            },
            section2 {
              title,
              description,
              imagePosition,
              image
            },
            section3 {
              title,
              description,
              imagePosition,
              image
            },
            section4 {
              title,
              description,
              imagePosition,
              image
            }
          },
          sections[] {
            title,
            description,
            imagePosition,
            image
          }
        },
        featuresTab {
          title,
          features[] {
            title,
            body,
            image
          },
          referencedFeatures[]-> {
            _id,
            "title": ${featureLangFields.title},
            "body": ${featureLangFields.body},
            image {
              asset-> {
                _id,
                url,
                metadata {
                  dimensions
                }
              },
              "alt": ${featureLangFields.alt}
            },
            categories
          }
        },
        geometryTab {
          title,
          image,
          description,
          geometryTable {
            title,
            rows[] {
              dimension,
              code,
              brgxs,
              brgs,
              brgm,
              brgl,
              brgxl,
              brgxxl
            }
          }
        },
        certificationsTab {
          title,
          image,
          description
        },
        picturesTab {
          title,
          images[]
        }
      },
      reviewsSection {
        title,
        reviews[] {
          customerName,
          country,
          bikeType,
          rating,
          reviewText,
          customerImages
        }
      }
    }`;
    
    let data = await currentClient.fetch(query, { locale });
    
    // Fallback to English if no content found in requested locale
    if (!data && locale !== 'en') {
      console.warn(`No gravel page content found for locale '${locale}', falling back to English`);
      query = query.replace('&& language == $locale', '&& language == "en"');
      data = await currentClient.fetch(query);
    }
    
    // Legacy fallback (content without language field)
    if (!data) {
      console.warn('No gravel page content found with language field, trying legacy format');
      query = query.replace('*[_type == "gravel" && language == "en"][0]', '*[_type == "gravel"][0]');
      data = await currentClient.fetch(query);
    }
    
    // Fetch additional reviews from review documents
    const additionalReviews = await getReviewsByCategory('Gravel');
    
    // Process the data with fallback defaults
    const processedData = {
      ...data,
      // Ensure heroSection has proper backgroundImage
      heroSection: {
        ...data?.heroSection,
        title: data?.heroSection?.title || "Branchy",
        buttonLabel: data?.heroSection?.buttonLabel || "Shop the Branchy",
        backgroundImage: ensureValidImageObject(data?.heroSection?.backgroundImage, "/images/gravel-hero-BikePacking.jpeg")
      },
      // Process product grid with fallbacks for configure card
      productGrid: {
        ...data?.productGrid,
        configureCard: {
          title: "Make it Yours",
          buttonText: "Configure",
          productLink: "/configure",
          ...data?.productGrid?.configureCard
        }
      },
      // Ensure tabs have proper image references
      tabs: {
        ...data?.tabs,
        featuresTab: {
          ...data?.tabs?.featuresTab,
          title: data?.tabs?.featuresTab?.title || "Features",
          features: data?.tabs?.featuresTab?.features || []
        },
        geometryTab: {
          ...data?.tabs?.geometryTab,
          title: data?.tabs?.geometryTab?.title || "Geometry",
          image: ensureValidImageObject(data?.tabs?.geometryTab?.image, "/images/gravel-geometry.png"),
          description: data?.tabs?.geometryTab?.description || null,
          geometryTable: data?.tabs?.geometryTab?.geometryTable || { title: "Geometry Table", rows: [] }
        },
        certificationsTab: {
          ...data?.tabs?.certificationsTab,
          title: data?.tabs?.certificationsTab?.title || "Certifications",
          image: ensureValidImageObject(data?.tabs?.certificationsTab?.image, "/images/gravel-certification.jpg"),
          description: data?.tabs?.certificationsTab?.description || null
        }
      },
      // Combine existing reviews with new review documents
      reviewsSection: {
        title: data?.reviewsSection?.title || 'Customer Reviews',
        reviews: [
          ...(data?.reviewsSection?.reviews || []), // Existing embedded reviews
          ...additionalReviews // New review documents
        ]
      }
    };
    
    return processedData;
  } catch (error) {
    console.error('Error fetching gravel page data:', error);
    return null;
  }
}

/**
 * Fetch MTB page data from Sanity
 */
export async function getMTBPageData(preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  const featureLangFields = getFeatureLanguageFields(locale);
  
  try {
    let query = `*[_type == "mtb" && language == $locale][0]{
      title,
      description,
      heroSection {
        title,
        buttonLabel,
        backgroundImage
      },
      productGrid {
        configureCard {
          title,
          buttonText,
          productLink,
          backgroundImage
        }
      },
      tabs {
        descriptionTab {
          title,
          defaultSections {
            section1 {
              title,
              description,
              imagePosition,
              image
            },
            section2 {
              title,
              description,
              imagePosition,
              image
            },
            section3 {
              title,
              description,
              imagePosition,
              image
            },
            section4 {
              title,
              description,
              imagePosition,
              image
            }
          },
          sections[] {
            title,
            description,
            imagePosition,
            image
          }
        },
        featuresTab {
          title,
          features[] {
            title,
            body,
            image
          },
          referencedFeatures[]-> {
            _id,
            "title": ${featureLangFields.title},
            "body": ${featureLangFields.body},
            image {
              asset-> {
                _id,
                url,
                metadata {
                  dimensions
                }
              },
              "alt": ${featureLangFields.alt}
            },
            categories
          }
        },
        geometryTab {
          title,
          image,
          description,
          geometryTable {
            title,
            rows[] {
              dimension,
              code,
              xs,
              s,
              m,
              l,
              xl,
              xxl
            }
          }
        },
        certificationsTab {
          title,
          image,
          description
        },
        picturesTab {
          title,
          images[]
        }
      },
      reviewsSection {
        title,
        reviews[] {
          customerName,
          country,
          bikeType,
          rating,
          reviewText,
          customerImages
        }
      }
    }`;
    
    let data = await currentClient.fetch(query, { locale });
    
    // Fallback to English if no content found in requested locale
    if (!data && locale !== 'en') {
      console.warn(`No MTB page content found for locale '${locale}', falling back to English`);
      query = query.replace('&& language == $locale', '&& language == "en"');
      data = await currentClient.fetch(query);
    }
    
    // Legacy fallback (content without language field)
    if (!data) {
      console.warn('No MTB page content found with language field, trying legacy format');
      query = query.replace('*[_type == "mtb" && language == "en"][0]', '*[_type == "mtb"][0]');
      data = await currentClient.fetch(query);
    }
    
    // Fetch additional reviews from review documents
    const additionalReviews = await getReviewsByCategory('MTB');
    
    // Process the data with fallback defaults
    const processedData = {
      ...data,
      // Ensure heroSection has proper backgroundImage
      heroSection: {
        ...data?.heroSection,
        title: data?.heroSection?.title || "YGGY",
        buttonLabel: data?.heroSection?.buttonLabel || "Shop the YGGY",
        backgroundImage: ensureValidImageObject(data?.heroSection?.backgroundImage, "/images/mtb-hero.jpeg")
      },
      // Process product grid with fallbacks for configure card
      productGrid: {
        ...data?.productGrid,
        configureCard: {
          title: "Make it Yours",
          buttonText: "Configure",
          productLink: "/configure",
          ...data?.productGrid?.configureCard
        }
      },
      // Ensure tabs have proper image references
      tabs: {
        ...data?.tabs,
        featuresTab: {
          ...data?.tabs?.featuresTab,
          title: data?.tabs?.featuresTab?.title || "Features",
          features: data?.tabs?.featuresTab?.features || []
        },
        geometryTab: {
          ...data?.tabs?.geometryTab,
          title: data?.tabs?.geometryTab?.title || "Geometry",
          image: ensureValidImageObject(data?.tabs?.geometryTab?.image, "/images/mtb-yggy-geometry.png"),
          description: data?.tabs?.geometryTab?.description || null,
          geometryTable: data?.tabs?.geometryTab?.geometryTable || { title: "Geometry Table", rows: [] }
        },
        certificationsTab: {
          ...data?.tabs?.certificationsTab,
          title: data?.tabs?.certificationsTab?.title || "Certifications",
          image: ensureValidImageObject(data?.tabs?.certificationsTab?.image, "/images/mtb-certification-yggy.jpg"),
          description: data?.tabs?.certificationsTab?.description || null
        }
      },
      // Combine existing reviews with new review documents
      reviewsSection: {
        title: data?.reviewsSection?.title || 'Customer Reviews',
        reviews: [
          ...(data?.reviewsSection?.reviews || []), // Existing embedded reviews
          ...additionalReviews // New review documents
        ]
      }
    };
    
    return processedData;
  } catch (error) {
    console.error('Error fetching MTB page data:', error);
    return null;
  }
}

// Helper function to fetch FAQ page data
export async function getFaqPageData(preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  
  try {
    let query = `*[_type == "faqPage" && language == $locale][0]{
      title,
      description,
      faqItems[]
    }`;
    
    let data = await currentClient.fetch(query, { locale });
    
    // Fallback to English if no content found in requested locale
    if (!data && locale !== 'en') {
      console.warn(`No FAQ page content found for locale '${locale}', falling back to English`);
      query = query.replace('&& language == $locale', '&& language == "en"');
      data = await currentClient.fetch(query);
    }
    
    // Legacy fallback (content without language field)
    if (!data) {
      console.warn('No FAQ page content found with language field, trying legacy format');
      query = query.replace('*[_type == "faqPage" && language == "en"][0]', '*[_type == "faqPage"][0]');
      data = await currentClient.fetch(query);
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching faqPage data:', error);
    return null;
  }
}

// Helper function to fetch design and engineering page data
export async function getDesignEngineeringPageData(preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  
  let query = `*[_type == "designEngineeringPage" && language == $locale][0]{
    title,
    description,
    heroSection,
    globalSection {
      section1,
      section2 {
        tableSection {
          title,
          description,
          tableRows[]
        }
      },
      section3,
      section4,
      section5,
      section6
    }
  }`;
  
  try {
    let data = await currentClient.fetch(query, { locale });
    
    // Fallback to English if no content found in requested locale
    if (!data && locale !== 'en') {
      console.warn(`No Design Engineering page content found for locale '${locale}', falling back to English`);
      query = query.replace('&& language == $locale', '&& language == "en"');
      data = await currentClient.fetch(query);
    }
    
    // Legacy fallback (content without language field)
    if (!data) {
      console.warn('No Design Engineering page content found with language field, trying legacy format');
      query = query.replace('*[_type == "designEngineeringPage" && language == "en"][0]', '*[_type == "designEngineeringPage"][0]');
      data = await currentClient.fetch(query);
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching Design & Engineering data:', error);
    return null;
  }
}

/**
 * Fetches "Our History" page data from Sanity
 * @returns {Promise<Object|null>} The Our History data or null if not found
 */
export async function getOurHistoryData(preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  
  try {
    let data = await currentClient.fetch(`
      *[_type == "ourHistory" && language == $locale][0] {
        title,
        heroImage,
        intro,
        earlyYearsParagraph,
        rowingImages[] {
          _type,
          asset->{
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt,
          caption,
          position
        },
        frameCracksParagraph,
        frameCrackImages[] {
          _type,
          asset->{
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt,
          caption,
          position
        },
        careerParagraph,
        cncParagraph,
        woodMaterialsParagraph,
        firstWorkshopParagraph,
        workshopImages[] {
          _type,
          asset->{
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt,
          caption,
          position
        },
        prototypesParagraph,
        prototypeImages[] {
          _type,
          asset->{
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt,
          caption,
          position
        },
        brandCreationParagraph,
        exhibitionImages[] {
          _type,
          asset->{
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt,
          caption,
          position
        },
        missionParagraph,
        currentWorkshopParagraph,
        currentWorkshopImages[] {
          _type,
          asset->{
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt,
          caption,
          position
        },
        conclusionParagraph
      }
    `, { locale });
    
    // Fallback to English if no content found in requested locale
    if (!data && locale !== 'en') {
      console.warn(`No Our History content found for locale '${locale}', falling back to English`);
      data = await currentClient.fetch(`
        *[_type == "ourHistory" && language == "en"][0] {
          title,
          heroImage,
          intro,
          earlyYearsParagraph,
          rowingImages[] {
            _type,
            asset->{
              _id,
              url,
              metadata {
                dimensions
              }
            },
            alt,
            caption,
            position
          },
          frameCracksParagraph,
          frameCrackImages[] {
            _type,
            asset->{
              _id,
              url,
              metadata {
                dimensions
              }
            },
            alt,
            caption,
            position
          },
          careerParagraph,
          cncParagraph,
          woodMaterialsParagraph,
          firstWorkshopParagraph,
          workshopImages[] {
            _type,
            asset->{
              _id,
              url,
              metadata {
                dimensions
              }
            },
            alt,
            caption,
            position
          },
          prototypesParagraph,
          prototypeImages[] {
            _type,
            asset->{
              _id,
              url,
              metadata {
                dimensions
              }
            },
            alt,
            caption,
            position
          },
          brandCreationParagraph,
          exhibitionImages[] {
            _type,
            asset->{
              _id,
              url,
              metadata {
                dimensions
              }
            },
            alt,
            caption,
            position
          },
          missionParagraph,
          currentWorkshopParagraph,
          currentWorkshopImages[] {
            _type,
            asset->{
              _id,
              url,
              metadata {
                dimensions
              }
            },
            alt,
            caption,
            position
          },
          conclusionParagraph
        }
      `);
    }
    
    // Legacy fallback (content without language field)
    if (!data) {
      console.warn('No Our History content found with language field, trying legacy format');
      data = await currentClient.fetch(`
        *[_type == "ourHistory"][0] {
          title,
          heroImage,
          intro,
          earlyYearsParagraph,
          rowingImages[] {
            _type,
            asset->{
              _id,
              url,
              metadata {
                dimensions
              }
            },
            alt,
            caption,
            position
          },
          frameCracksParagraph,
          frameCrackImages[] {
            _type,
            asset->{
              _id,
              url,
              metadata {
                dimensions
              }
            },
            alt,
            caption,
            position
          },
          careerParagraph,
          cncParagraph,
          woodMaterialsParagraph,
          firstWorkshopParagraph,
          workshopImages[] {
            _type,
            asset->{
              _id,
              url,
              metadata {
                dimensions
              }
            },
            alt,
            caption,
            position
          },
          prototypesParagraph,
          prototypeImages[] {
            _type,
            asset->{
              _id,
              url,
              metadata {
                dimensions
              }
            },
            alt,
            caption,
            position
          },
          brandCreationParagraph,
          exhibitionImages[] {
            _type,
            asset->{
              _id,
              url,
              metadata {
                dimensions
              }
            },
            alt,
            caption,
            position
          },
          missionParagraph,
          currentWorkshopParagraph,
          currentWorkshopImages[] {
            _type,
            asset->{
              _id,
              url,
              metadata {
                dimensions
              }
            },
            alt,
            caption,
            position
          },
          conclusionParagraph
        }
      `);
    }
    
    console.log('Fetched Our History data:', data ? 'Success' : 'Not found');
    return data || null;
  } catch (error) {
    console.error('Error fetching Our History data:', error);
    return null;
  }
}

/**
 * Fetches all blog post slugs for static path generation
 * @returns {Promise<Array<string>>} Array of post slugs
 */
export async function getAllBlogPostSlugs() {
  try {
    const slugs = await client.fetch(`
      *[_type == "post"].slugMultilingual.current
    `);

    // Normalize slugs to ensure they don't have leading slashes
    const normalizedSlugs = slugs.map(slug => 
      slug.startsWith('/') ? slug.substring(1) : slug
    );

    console.log('Fetched all blog post slugs:', normalizedSlugs);
    return normalizedSlugs || [];
  } catch (error) {
    console.error('Error fetching blog post slugs:', error);
    return [];
  }
}

/**
 * Fetches a single blog post by slug
 * @param {string} slug - The post slug
 * @returns {Promise<Object|null>} The post object or null if not found
 */
export async function getBlogPost(slug, preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  
  try {
    // Handle null or undefined slug
    if (!slug) {
      console.error('getBlogPost called with null or undefined slug');
      return null;
    }

    // Remove leading and trailing slashes if present
    const normalizedSlug = slug.toString().replace(/^\/+|\/+$/g, '');
    
    console.log(`Attempting to fetch blog post with normalized slug: "${normalizedSlug}" for locale: ${locale}`);
    
    // Query using the new multilingual field structure
    const posts = await currentClient.fetch(`
      *[_type == "post" && (slugMultilingual.current == $slug || slugMultilingual.current == '/' + $slug)] {
        _id,
        _type,
        "title": Titles.${locale},
        "slug": {
          "current": slugMultilingual.current
        },
        "publishedAt": publishedAtM,
        "excerpt": excerptMultilingual.${locale},
        "mainImage": mainImageMultilingual,
        "authorName": authorNameM,
        "body": body${locale.toUpperCase()},
        "titleFont": titleFontMultilingual,
        "viewCount": coalesce(viewCount, 0)
      }
    `, { slug: normalizedSlug });

    const post = posts && posts.length > 0 ? posts[0] : null;
    
    if (post) {
      console.log(`Found blog post: ${post.title} (${post._id}) of type ${post._type}`);
    } else {
      console.error(`No blog post found with slug: "${normalizedSlug}"`);
      
      // Log available posts for debugging
      const allPosts = await client.fetch(`*[_type == "post"] { _id, "title": Titles.en, "slug": slugMultilingual.current }`);
      console.log('Available posts:', allPosts);
    }
    
    return post;
  } catch (error) {
    console.error(`Error fetching blog post with slug ${slug}:`, error);
    return null;
  }
}

/**
 * Fetches all blog posts from Sanity
 * @returns {Promise<Array>} Array of blog posts
 */
export async function getBlogPosts(preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  
  try {
    // Query using the new multilingual field structure
    const posts = await currentClient.fetch(`
      *[_type == "post"] | order(publishedAtM desc) {
        _id,
        "title": Titles.${locale},
        "slug": {
          "current": slugMultilingual.current
        },
        "publishedAt": publishedAtM,
        "excerpt": excerptMultilingual.${locale},
        "authorName": authorNameM,
        "mainImage": mainImageMultilingual,
        "titleFont": titleFontMultilingual,
        "viewCount": coalesce(viewCount, 0)
      }
    `);

    console.log('Fetched blog posts:', posts?.length || 0);
    
    // Ensure slugs are formatted correctly and filter out posts without required content
    const normalizedPosts = posts
      .filter(post => post.title && post.excerpt) // Only include posts with content in the requested language
      .map(post => {
        // Ensure the slug doesn't have a leading slash
        if (post.slug?.current?.startsWith('/')) {
          post.slug.current = post.slug.current.substring(1);
        }
        return post;
      });

    return normalizedPosts || [];
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    return [];
  }
}


/**
 * Fetches blog settings from Sanity with field-level translation support
 * @returns {Promise<Object|null>} The blog settings or null if not found
 */
export async function getBlogSettings(preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  
  try {
    // Fetch the shared blog settings document with all multilingual fields
    let settings = await currentClient.fetch(`
      *[_type == "blogSettings"][0] {
        titles,
        descriptions,
        heroImage {
          asset,
          altTexts
        }
      }
    `);
    
    if (settings) {
      // Process the data to extract the appropriate language version
      const processedSettings = {
        title: settings.titles?.[locale] || settings.titles?.en || 'Our Blog',
        description: settings.descriptions?.[locale] || settings.descriptions?.en || 'Stories and insights about our wooden bikes, craftsmanship, and sustainable biking adventures',
        heroImage: settings.heroImage ? {
          ...settings.heroImage,
          alt: settings.heroImage.altTexts?.[locale] || settings.heroImage.altTexts?.en || 'WoodAlps Blog Hero Image'
        } : null
      };
      
      console.log(`✅ Fetched shared blog settings with ${locale} content`);
      return processedSettings;
    }
    
    console.error('No blog settings found');
    return null;
  } catch (error) {
    console.error('Error fetching blog settings:', error);
    return null;
  }
}

/**
 * Fetches all gallery items from Sanity
 * @returns {Promise<Array>} Array of gallery items
 */
export async function getGalleryItems(preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  
  try {
    // Fetch all gallery items (no longer language-specific, they're shared across languages)
    let items = await currentClient.fetch(`
      *[_type == "galleryItem"] | order(order asc) {
        _id,
        image {
          asset,
          altTexts
        },
        "imageMetadata": image.asset->metadata,
        captions,
        order,
        orientation,
      }
    `);
    
    // Process items to extract the correct caption and alt text for the current locale
    if (items && items.length > 0) {
      items = items.map(item => ({
        ...item,
        caption: item.captions?.[locale] || '', // Only show caption if it exists in current language
        image: {
          ...item.image,
          alt: item.image?.altTexts?.[locale] || item.image?.altTexts?.en || '' // Alt text still fallback to English for accessibility
        }
      }));
      
      console.log(`✅ Fetched ${items.length} shared gallery items with ${locale} captions`);
    }

    return items || [];
  } catch (error) {
    console.error('Error fetching gallery items:', error);
    return [];
  }
}

/**
 * Fetches all engraving items from Sanity
 * @returns {Promise<Array>} Array of engraving items
 */
export async function getEngravingItems(preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  
  try {
    // Fetch all engraving items (no longer language-specific, they're shared across languages)
    let items = await currentClient.fetch(`
      *[_type == "engravingItem"] | order(order asc) {
        _id,
        image {
          asset,
          altTexts
        },
        "imageMetadata": image.asset->metadata,
        captions,
        order,
        orientation,
      }
    `);
    
    // Process items to extract the correct caption and alt text for the current locale
    if (items && items.length > 0) {
      items = items.map(item => ({
        ...item,
        caption: item.captions?.[locale] || '', // Only show caption if it exists in current language
        image: {
          ...item.image,
          alt: item.image?.altTexts?.[locale] || item.image?.altTexts?.en || '' // Alt text still fallback to English for accessibility
        }
      }));
      
      console.log(`✅ Fetched ${items.length} shared engraving items with ${locale} captions`);
    }

    return items || [];
  } catch (error) {
    console.error('Error fetching engraving items:', error);
    return [];
  }
}

/**
 * Fetches approved reviews by target category from Sanity
 * @param {string} category - The target category (Road, Gravel, MTB)
 * @returns {Promise<Array>} Array of approved reviews
 */
export async function getReviewsByCategory(category) {
  try {
    const reviews = await client.fetch(`
      *[_type == "review" && targetCategory == $category && status == "approved"] | order(submittedAt desc) {
        customerName,
        country,
        bikeModel,
        bikeSize,
        bikeType,
        rating,
        reviewText,
        customerImages,
        submittedAt
      }
    `, { category });
    
    console.log(`Fetched ${category} reviews:`, reviews?.length || 0);
    return reviews || [];
  } catch (error) {
    console.error(`Error fetching ${category} reviews:`, error);
    return [];
  }
}

/**
 * Helper function to get language-specific field names
 * @param {string} locale - The locale (en, fr, de, it)
 * @returns {object} Object with language-specific field names
 */
function getLanguageFields(locale = 'en') {
  const langSuffix = locale.toUpperCase();
  return {
    title: `title${langSuffix}`,
    quote: `quote${langSuffix}`,
    story: `story${langSuffix}`
  };
}

/**
 * Helper function to get language-specific field names for features
 * @param {string} locale - The locale (en, fr, de, it)
 * @returns {Object} Object with language-specific field names
 */
function getFeatureLanguageFields(locale = 'en') {
  const langSuffix = locale.toUpperCase();
  return {
    title: `title${langSuffix}`,
    body: `body${langSuffix}`,
    alt: `alt${langSuffix}`
  };
}

/**
 * Helper function to get language-specific field names for products
 * @param {string} locale - The locale (en, fr, de, it)
 * @returns {Object} Object with language-specific field names
 */
function getProductLanguageFields(locale = 'en') {
  const langSuffix = locale.toUpperCase();
  return {
    title: `title${langSuffix}`,
    shortBlurb: `shortBlurb${langSuffix}`,
    cardShortDescriptionBold: `cardShortDescriptionBold${langSuffix}`,
    cardShortDescriptionGrey: `cardShortDescriptionGrey${langSuffix}`,
    material: `material${langSuffix}`,
    sectionTitle: `sectionTitle${langSuffix}`,
    groupTitle: `groupTitle${langSuffix}`,
    specKey: `key${langSuffix}`,
    overview: `overview${langSuffix}`
  };
}

/**
 * Fetches all active ambassadors from Sanity
 * @param {boolean} preview - Whether to use preview mode
 * @param {string} locale - The locale to fetch content for
 * @returns {Promise<Array>} Array of ambassador data
 */
export async function getAmbassadors(preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  const langFields = getLanguageFields(locale);
  
  try {
    // Fetch ambassadors without language filter since we're using field-level internationalization
    let ambassadors = await currentClient.fetch(`
      *[_type == "ambassador" && active == true] | order(displayOrder asc, joiningDate desc) {
        _id,
        fullName,
        "title": ${langFields.title},
        location,
        joiningDate,
        bikeCategory,
        bikeSize,
        bikeName,
        "quote": ${langFields.quote},
        portraitImage {
          asset-> {
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt
        },
        actionImages[] {
          asset-> {
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt
        },
        "story": ${langFields.story},
        address,
        socialLinks {
          instagram,
          facebook,
          strava,
          website
        },
        displayOrder
      }
    `);
        
    console.log('Fetched ambassadors:', ambassadors?.length || 0);
    return ambassadors || [];
  } catch (error) {
    console.error('Error fetching ambassadors:', error);
    return [];
  }
}

/**
 * Fetches ambassadors filtered by bike category
 * @param {string} category - The bike category (Road, Gravel, MTB)
 * @param {boolean} preview - Whether to use preview mode
 * @param {string} locale - The locale to fetch content for
 * @returns {Promise<Array>} Array of filtered ambassador data
 */
export async function getAmbassadorsByCategory(category, preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  const langFields = getLanguageFields(locale);
  
  try {
    // Fetch ambassadors by category without language filter since we're using field-level internationalization
    let ambassadors = await currentClient.fetch(`
      *[_type == "ambassador" && active == true && bikeCategory == $category] | order(displayOrder asc, joiningDate desc) {
        _id,
        fullName,
        "title": ${langFields.title},
        location,
        joiningDate,
        bikeCategory,
        bikeSize,
        bikeName,
        "quote": ${langFields.quote},
        portraitImage {
          asset-> {
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt
        },
        actionImages[] {
          asset-> {
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt
        },
        "story": ${langFields.story},
        address,
        socialLinks {
          instagram,
          facebook,
          strava,
          website
        },
        displayOrder
      }
    `, { category });
    

    
    
    console.log(`Fetched ${category} ambassadors:`, ambassadors?.length || 0);
    return ambassadors || [];
  } catch (error) {
    console.error(`Error fetching ${category} ambassadors:`, error);
    return [];
  }
}

/**
 * Fetches a single ambassador by ID
 * @param {string} id - The ambassador ID
 * @param {boolean} preview - Whether to use preview mode
 * @param {string} locale - The locale to fetch content for
 * @returns {Promise<Object|null>} The ambassador object or null if not found
 */
export async function getAmbassador(id, preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  const langFields = getLanguageFields(locale);
  
  try {
    if (!id) {
      console.error('getAmbassador called with null or undefined ID');
      return null;
    }
    
    console.log(`Attempting to fetch ambassador with ID: "${id}"`);
    
    const ambassador = await currentClient.fetch(`
      *[_type == "ambassador" && _id == $id][0] {
        _id,
        fullName,
        "title": ${langFields.title},
        location,
        joiningDate,
        bikeCategory,
        bikeSize,
        bikeName,
        "quote": ${langFields.quote},
        portraitImage {
          asset-> {
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt
        },
        actionImages[] {
          asset-> {
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt
        },
        "story": ${langFields.story},
        address,
        socialLinks {
          instagram,
          facebook,
          strava,
          website
        },
        active,
        displayOrder
      }
    `, { id });
    
    if (ambassador) {
      console.log(`Found ambassador: ${ambassador.fullName} (${ambassador._id})`);
    } else {
      console.error(`No ambassador found with ID: "${id}"`);
    }
    
    return ambassador;
  } catch (error) {
    console.error(`Error fetching ambassador with ID ${id}:`, error);
    return null;
  }
}

/**
 * Fetches top ambassadors for homepage display (ordered by displayOrder)
 * @param {number} limit - Maximum number of ambassadors to return
 * @param {boolean} preview - Whether to use preview mode
 * @param {string} locale - The locale to fetch content for
 * @returns {Promise<Array>} Array of top ambassador data
 */
export async function getTopAmbassadors(limit = 3, preview = false, locale = 'en') {
  const currentClient = getClient(preview);
  const langFields = getLanguageFields(locale);
  
  try {
    const ambassadors = await currentClient.fetch(`
      *[_type == "ambassador" && active == true] | order(displayOrder asc, joiningDate desc) [0...${limit}] {
        _id,
        fullName,
        "title": ${langFields.title},
        location,
        bikeCategory,
        bikeSize,
        bikeName,
        "quote": ${langFields.quote},
        portraitImage {
          asset-> {
            _id,
            url,
            metadata {
              dimensions
            }
          },
          alt
        },
        socialLinks {
          instagram,
          facebook,
          strava,
          website
        }
      }
    `);
    
    console.log(`Fetched top ambassadors:`, ambassadors?.length || 0);
    return ambassadors || [];
  } catch (error) {
    console.error('Error fetching top ambassadors:', error);
    return [];
  }
}